"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from "lucide-react";
import { useLanguage, languages, type Language } from "@/lib/language-context";

export function LanguageSwitcher({ profileHref }: { profileHref?: string }) {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {profileHref ? (
        <div className="inline-flex items-stretch rounded-md border border-input bg-transparent shadow-xs">
          <a
            href={profileHref}
            aria-label="Open profile"
            className="inline-flex min-h-9 items-center justify-center rounded-l-md px-3 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Globe className="h-4 w-4" />
          </a>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-l-none border-l border-input bg-transparent px-3 hover:bg-accent"
            >
              <span className="hidden sm:inline">{languages[language].name}</span>
              <span className="sm:hidden">{languages[language].flag}</span>
            </Button>
          </DropdownMenuTrigger>
        </div>
      ) : (
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 bg-transparent">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">{languages[language].name}</span>
            <span className="sm:hidden">{languages[language].flag}</span>
          </Button>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="end" className="w-48">
        {Object.entries(languages).map(([code, lang]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => {
              setLanguage(code as Language);
              setIsOpen(false);
            }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <span className="text-lg">{lang.flag}</span>
            <span>{lang.name}</span>
            {language === code && (
              <span className="ml-auto text-blue-600">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
